/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommsTemplateType } from '@src/app/model/CommsTemplate';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { RequestService } from '@src/app/modules/crm/request.service';

import { Supplier } from '@src/app/model/supplier';
import { MatTooltip } from '@angular/material/tooltip';
import { SupplierService } from '@src/app/modules/supply/supplier.service';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { MatIconModule } from '@angular/material/icon';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormEmailComponent } from '@src/app/shared/form/form-email/form-email.component';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { NgTemplateOutlet } from '@angular/common';
import { MessageService } from '@src/app/shared/message.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { Attachment } from '@src/app/model/attachment';
import { MailAttachmentChipsComponent, MAX_MAIL_ATTACHMENT_BYTES } from '@src/app/shared/mail-attachment-chips/mail-attachment-chips.component';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { PreviewMailDialogComponent } from '@src/app/modules/social/comms-template-page/preview-mail-dialog.component';
import { required } from '@src/app/shared/validators';
import { Observable, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';

type SendMailMode = 'send' | 'test' | 'attach';

@Component({
    selector: 'app-send-supplier',
    imports: [DialogBannerComponent, MatDialogModule, CtlHolderComponent, NgTemplateOutlet,
        MatButtonModule, MatTooltip, MatIconModule, MailAttachmentChipsComponent],
    templateUrl: './send-supplier.component.html',
    styleUrl: './send-supplier.component.scss'
})
export class SendSupplierComponent {

    dialogRef = inject(MatDialogRef);
    sr = inject(MAT_DIALOG_DATA);
    srSvc = inject(RequestService);
    supplierSvc = inject(SupplierService);
    dialog = inject(MatDialog);
    msgSvc = inject(MessageService);
    cds = inject(ConfirmDialogService);
    currentUser = inject(CurrentUserService).currentUser;

    toSuppliers: Supplier[] = [];

    supplierField = FormComboBoxComponent.make('Supplier', 'supplierId', 'supplier', {
        service: this.supplierSvc,
    }).setupControl();

    mailText = FormRichTextComponent.make('Mail Text', 'mailText', { validators: [required] })
        .setupControl()
        .setCommsTemplate(CommsTemplateType.REQUEST);

    selectedAttachments: Attachment[] = [];
    isSending = false;

    get hasEmailableSuppliers(): boolean {
        return this.toSuppliers.length > 0;
    }

    get attachmentSizeExceeded(): boolean {
        return this.selectedAttachments.reduce((sum, a) => sum + (a.size || 0), 0) > MAX_MAIL_ATTACHMENT_BYTES;
    }

    get actionsDisabled(): boolean {
        return !this.mailText.control.valid || this.attachmentSizeExceeded || this.isSending;
    }

    readonly previewTooltip = $localize`Preview the merged content and any selected attachments — nothing is sent or saved.`;
    readonly sendTooltip = $localize`Send the email to the selected supplier(s) and record it against this request.`;
    readonly testTooltip = $localize`Send a test copy to your own address (${this.currentUser?.email}) so you can check it. Nothing is sent to the suppliers and nothing is recorded.`;
    readonly attachTooltip = $localize`Generate a PDF from the content and attach it to this request. No email will be sent.`;

    constructor() {
        if (this.sr.supplierId) {
            this.insertSupplier(this.sr.supplier);
        } else {
            this.addSupplier();
        }
    }

    removeSupplier(s: Supplier) {
        this.toSuppliers = this.toSuppliers.filter(sup => sup.id !== s.id);
    }

    pickSupplierFormConfig(): FormConfig {
        return new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FormTextComponent.make('Name', 'name'),
                    FormTextComponent.make('Contact', 'contactPerson'),
                    FormPhoneComponent.make('Code', 'code'),
                    FormPhoneComponent.make('Phone', 'phone'),
                    FormEmailComponent.make('Email', 'email'),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
            title: $localize`Suppliers`,
            mode: 'list',
        });
    }

    insertSupplier(supplier: Supplier) {
        if (!supplier) return;
        if (supplier.omcTeamId && supplier.omcTeamId !== this.sr.teamId) {
            this.cds.alert($localize`Wrong OMC`,
                $localize`Cannot add supplier ${supplier.name} to this request, they only work with ${supplier.omcTeamId}`);
            return;
        }
        if (!supplier.email) {
            this.cds.alert($localize`No Email Address`,
                $localize`${supplier.name} does not have an email address and cannot be added as a recipient`);
            return;
        }
        if (!this.toSuppliers.find(s => s.id === supplier.id)) {
            this.toSuppliers.push(supplier);
        }
    }

    addSupplier() {
        const dialogRef = this.dialog.open(PickDialogComponent, {
            data: {
                config: this.pickSupplierFormConfig(),
                service: this.supplierSvc,
                showSearch: true,
            }
        });
        dialogRef.afterClosed().subscribe(s => this.insertSupplier(s));
    }

    doPreview() {
        this.srSvc.previewSupplierMail(this.sr, this.mailText.control.value, this.selectedAttachments)
            .subscribe(result => {
                if (result) {
                    this.dialog.open(PreviewMailDialogComponent,
                        { data: { title: $localize`Preview`, html: result.html }, width: '80vw', maxWidth: '80vw' });
                }
            });
    }

    confirmSend() {
        const emails = this.toSuppliers.map(s => s.email).join(', ');
        const msg = $localize`Messages will be sent to ${emails}. ${this.selectedAttachments.length} attachment(s) will be included.`;
        this.cds.openConfirm($localize`Send mails to suppliers?`, msg)
            .subscribe(r => { if (r === 'OK') { this.submit('send'); } });
    }

    confirmTest() {
        this.cds.openConfirm($localize`Send Test Email?`, this.testTooltip)
            .subscribe(r => { if (r === 'OK') { this.submit('test'); } });
    }

    confirmAttach() {
        this.cds.openConfirm($localize`Attach as PDF?`, this.attachTooltip)
            .subscribe(r => { if (r === 'OK') { this.submit('attach'); } });
    }

    private submit(mode: SendMailMode) {
        this.isSending = true;
        this.srSvc.sendSupplierMails(this.sr, this.mailText.control.value, mode, this.toSuppliers, this.selectedAttachments)
            .pipe(concatMap(result => this.afterSubmit(mode, result)))
            .subscribe({
                next: result => {
                    this.isSending = false;
                    this.msgSvc.show(this.resultMessage(mode));
                    if (mode !== 'test') {
                        this.dialogRef.close(result);
                    }
                },
                error: f => {
                    this.msgSvc.showError(f, 'Sending Supplier Mails');
                    this.isSending = false;
                },
            });
    }

    /** Only a real send is worth waiting on - a test goes to us, an attach mails nobody. */
    private afterSubmit(mode: SendMailMode, result: unknown): Observable<unknown> {
        if (mode === 'send') {
            return this.srSvc.promptExpectResponse(this.sr);
        }
        return of(result);
    }

    private resultMessage(mode: SendMailMode): string {
        if (mode === 'test') {
            return $localize`Test sent to ${this.currentUser?.email} — check your inbox.`;
        }
        if (mode === 'attach') {
            return $localize`The request content has been saved as a PDF and attached to this request.`;
        }
        return $localize`Mails sent successfully`;
    }
}
